import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivityChecklistItem } from '@linktivity/ars-neo';
import Attachment from '@link/share/components/Attachment';
import ReactMarkdown from '@link/share/components/ReactMarkdown';
import type { PlanDetailProps } from './PlanDetail.types';
import styles from './planDetail.module.css';

/**
 * Everything the product says about a plan, as text.
 *
 * All product data, so it renders on the server: the page puts every plan's
 * details in its HTML, and the details drawer shows the same component.
 */
const PlanDetail = ({ plan }: PlanDetailProps) => {
  const { t } = useTranslation();
  const label = (key: string) => t(`components.planDetailDrawer.${key}`);
  const locations = plan.participationLocations ?? [];
  const inclusions = plan.inclusions ?? [];
  const { maxCapacityInclusive: max, minCapacityInclusive: min } = plan;

  /** One titled list in a plan's details; `items` are `<li>`s. */
  const renderSection = useCallback(
    (title: string, items: ReactNode) => (
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <ul className={styles.list}>{items}</ul>
      </section>
    ),
    []
  );

  /** Restrictions, what to bring, what to wear…: text with attachments. */
  const renderChecklist = useCallback(
    (title: string, items: ActivityChecklistItem[] | undefined) =>
      items?.length
        ? renderSection(
            title,
            items.map((item, index) => (
              <li key={index}>
                <ReactMarkdown source={item.description ?? ''} />
                <Attachment attachments={item.attachments} />
              </li>
            ))
          )
        : null,
    [renderSection]
  );

  return (
    <div className={styles.detail}>
      <h2 className={styles.title}>{plan.title}</h2>
      {plan.description && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{label('planContent')}</h3>
          <ReactMarkdown source={plan.description} className={styles.text} />
        </section>
      )}
      {(!!max || !!min) &&
        renderSection(
          label('capacity'),
          <>
            {!!max && (
              <li>
                {label('maxCapacity')}: {t('share.common.space')} {max}
              </li>
            )}
            {!!min && (
              <li>
                {label('minCapacity')}: {t('share.common.space')} {min}
              </li>
            )}
          </>
        )}
      {plan.validityPeriod?.description &&
        renderSection(
          label('voucherExpirationPeriod'),
          <li>
            <ReactMarkdown source={plan.validityPeriod.description} />
          </li>
        )}
      {inclusions.length > 0 &&
        renderSection(
          label('inclusion'),
          inclusions.map((inclusion, index) => (
            <li key={index}>
              <ReactMarkdown source={inclusion.description ?? ''} />
            </li>
          ))
        )}
      {locations.length > 0 &&
        renderSection(
          label('participationLocations'),
          locations.map((location, index) => (
            <li key={index}>
              <p className={styles.subtitle}>
                {label(
                  `participationLocationKind.${location.kind || 'MAP_OF_MEETING_POINT'}`
                )}
              </p>
              <ReactMarkdown source={location.description ?? ''} />
              <Attachment attachments={location.attachments} />
            </li>
          ))
        )}
      {renderChecklist(label('restrictions'), plan.restrictionsWithAttachments)}
      {renderChecklist(
        label('requirements'),
        plan.requiredInfoItemsWithAttachments
      )}
      {renderChecklist(label('attireItems'), plan.attireItemsWithAttachments)}
      {renderChecklist(label('otherInfo'), plan.otherInfoItemsWithAttachments)}
    </div>
  );
};

export default PlanDetail;
